import { useState } from "react";
import { toast } from "react-toastify";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  CheckCircle2,
  User,
  Building2,
  ArrowRight,
  Headphones,
  Sparkles,
  ShieldCheck,
  Globe,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import contactimage from "../assets/contact.jpeg"; // adjust the depth to match your folder
import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "./Seo"; // same folder — adjust if yours differs
import { ROUTES, organizationSchema } from "../seoData";

const PATH = "/contact";
const TITLE = ROUTES[PATH].title;
const DESCRIPTION = ROUTES[PATH].description;

const CONTACT_INFO = [
  {
    icon: Mail,
    title: "Email support",
    content: "info@deetyasoft.com",
    sub: "Response within 24 hours",
    href: "mailto:info@deetyasoft.com",
  },
  {
    icon: Phone,
    title: "Call support",
    content: "+91 90153 50505",
    sub: "Mon–Sat · 9 am – 6 pm IST",
    href: "tel:+919015350505",
  },
  {
    icon: MapPin,
    title: "Visit us",
    content: "Mahalaxmi Square",
    sub: "Indrapuram, Ghaziabad",
    href: "https://maps.google.com/?q=Mahalaxmi+Square+Indrapuram+Ghaziabad",
  },
  {
    icon: Clock,
    title: "Business hours",
    content: "Mon – Sat",
    sub: "9:00 am – 6:00 pm IST",
  },
];

const QUICK_STATS = [
  { icon: Headphones, label: "24/7 support" },
  { icon: CheckCircle2, label: "Fast response" },
  { icon: Globe, label: "Global reach" },
  { icon: ShieldCheck, label: "Enterprise-grade" },
];

const HIGHLIGHTS = [
  "Expert consultation & guidance",
  "Tailored solutions for your business",
  "Dedicated support team",
];

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${SITE_URL}${PATH}#contactpage`,
    url: `${SITE_URL}${PATH}`,
    name: TITLE,
    description: DESCRIPTION,
    inLanguage: "en-IN",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    breadcrumb: { "@id": `${SITE_URL}${PATH}#breadcrumb` },
    mainEntity: { "@id": `${SITE_URL}/#organization` },
  },
  organizationSchema(),
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${SITE_URL}${PATH}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Contact", item: `${SITE_URL}${PATH}` },
    ],
  },
];

const SectionHeading = ({ id, pill, icon: Icon, before, highlight, after, text }) => (
  <div className="mx-auto mb-10 max-w-3xl text-center lg:mb-14">
    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-3 py-1 text-[11px] font-medium text-[#48247f] shadow-sm sm:text-xs">
      {Icon ? <Icon className="h-3.5 w-3.5 text-[#702cb0]" /> : <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#12dfbd]" />}
      {pill}
    </div>
    <h2
      id={id}
      className="text-balance text-[24px] font-light leading-[1.25] tracking-tight text-[#28194f] sm:text-[30px] md:text-[34px] lg:text-[38px]"
    >
      {before}{" "}
      <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
        {highlight}
      </span>
      {after ? ` ${after}` : ""}
    </h2>
    {text && (
      <p className="mx-auto mt-4 max-w-[640px] text-sm leading-[1.75] text-[#3b2a63] sm:text-[15px] md:text-base">
        {text}
      </p>
    )}
    <div className="mx-auto mt-6 h-1 w-24 rounded-full bg-gradient-to-r from-[#12dfbd] via-[#702cb0] to-[#48247f]" />
  </div>
);

const Contact = () => {
  const reduced = useReducedMotion();
  const initial = reduced ? false : "hidden";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    agreements: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: formData.message,
        companyName: formData.company,
        subject: formData.subject,
        source: "LmsBots.com",
        agreements: formData.agreements,
      };

      const response = await fetch("https://api.dndfilter.com/api/users/enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        toast.success("Enquiry sent successfully");
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
          agreements: false,
        });
      } else {
        throw new Error(data.message || "Something went wrong");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to send enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants (respect reduced-motion)
  const fadeUp = reduced
    ? undefined
    : {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
      };
  const stagger = reduced
    ? undefined
    : { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path={PATH} jsonLd={JSON_LD} />

      <div
        className="w-full overflow-x-clip bg-white font-sans text-[#28194f] antialiased"
      >
        {/* ================= HERO ================= */}
        <section
          aria-labelledby="contact-heading"
          className="relative isolate w-full overflow-hidden bg-gradient-to-b from-[#f6f3fc] via-white to-white"
        >
          <div
            className="pointer-events-none absolute inset-0 -z-10 opacity-40"
            style={{
              backgroundImage: "radial-gradient(#48247f18 1px, transparent 1px)",
              backgroundSize: "22px 22px",
              WebkitMaskImage: "radial-gradient(ellipse at 40% 40%, black 30%, transparent 80%)",
              maskImage: "radial-gradient(ellipse at 40% 40%, black 30%, transparent 80%)",
            }}
          />
          <div className="pointer-events-none absolute -right-24 -top-24 -z-10 h-72 w-72 rounded-full bg-[#702cb0]/10 blur-3xl sm:h-96 sm:w-96" />
          <div className="pointer-events-none absolute -left-24 bottom-0 -z-10 h-64 w-64 rounded-full bg-[#12dfbd]/12 blur-3xl sm:h-80 sm:w-80" />

          <div className="relative z-10 mx-auto grid w-full max-w-[1500px] items-center gap-10 px-4 pb-14 pt-20 sm:gap-12 sm:px-8 sm:pb-16 sm:pt-24 md:px-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:px-14 lg:pb-24 lg:pt-32 xl:px-20">
            <motion.div
              initial={initial}
              animate="visible"
              variants={stagger}
              className="mx-auto max-w-xl text-center lg:mx-0 lg:text-left"
            >
              <motion.div variants={fadeUp}>
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-3 py-1 text-[11px] font-medium text-[#48247f] shadow-sm sm:mb-6 sm:text-xs">
                  <Sparkles className="h-3.5 w-3.5 text-[#702cb0]" />
                  Contact LMSBOTS
                </div>
              </motion.div>

              <motion.h1
                id="contact-heading"
                variants={fadeUp}
                className="text-balance text-[26px] font-light leading-[1.2] tracking-tight sm:text-[32px] md:text-[36px] lg:text-[40px] xl:text-[44px]"
              >
                Let's build something{" "}
                <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                  great together
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mx-auto mt-4 max-w-[560px] text-sm font-light leading-[1.75] text-[#3b2a63] sm:text-base lg:mx-0 lg:text-[17px]"
              >
                Have a question about our CRM or need a tailored demo? Our team responds within 24 hours
                and is happy to walk you through the platform.
              </motion.p>

              <motion.ul
                variants={fadeUp}
                className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2.5 sm:gap-x-5 lg:justify-start"
              >
                {QUICK_STATS.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="inline-flex items-center gap-2 text-[11px] font-medium text-[#3b2a63] sm:text-xs md:text-sm"
                  >
                    <Icon className="h-3.5 w-3.5 text-[#0f9d86]" strokeWidth={2.4} />
                    {label}
                  </li>
                ))}
              </motion.ul>
            </motion.div>

            <motion.div
              initial={reduced ? false : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="relative mx-auto w-full max-w-[600px] px-3 pb-6 pt-3 lg:max-w-none"
            >
              <div className="absolute inset-x-8 bottom-0 top-10 rounded-3xl bg-[#e3faf5]" />
              <div className="relative rounded-2xl bg-white p-2 shadow-xl ring-1 ring-slate-200">
                <img
                  src={contactimage}
                  alt="LMSBOTS contact and support team"
                  width="1200"
                  height="800"
                  fetchPriority="high"
                  decoding="async"
                  className="h-auto w-full rounded-xl border border-slate-200 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ================= MAIN CONTENT (info + form) ================= */}
        <section
          aria-labelledby="contact-main-heading"
          className="relative w-full bg-white py-12 sm:py-16 lg:py-24"
        >
          <div className="mx-auto w-full max-w-[1300px] px-4 sm:px-8 md:px-10 lg:px-14">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
              {/* LEFT — info column */}
              <motion.div
                initial={initial}
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="lg:col-span-5"
              >
                {/* Intro */}
                <motion.div
                  variants={fadeUp}
                  className="rounded-2xl border border-[#48247f]/10 bg-white p-6 shadow-[0_8px_30px_-12px_rgba(72,36,127,0.22)] sm:p-8"
                >
                  <h2
                    id="contact-main-heading"
                    className="text-balance text-[22px] font-light leading-[1.25] tracking-tight text-[#28194f] sm:text-[26px] md:text-[30px]"
                  >
                    Start a{" "}
                    <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                      conversation
                    </span>
                  </h2>
                  <p className="mt-3 text-sm leading-[1.75] text-[#3b2a63] sm:text-[15px]">
                    Whether you're improving customer engagement, automating
                    messaging, or scaling your WhatsApp strategy, our team is
                    ready to help.
                  </p>

                  <ul className="mt-6 space-y-3">
                    {HIGHLIGHTS.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm font-medium text-[#28194f]">
                        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#f1ecfa] text-[#48247f]">
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={3} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Contact cards */}
                <motion.ul
                  variants={stagger}
                  initial={initial}
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                  className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2"
                >
                  {CONTACT_INFO.map((info) => {
                    const Icon = info.icon;
                    const Wrapper = info.href ? "a" : "div";
                    const wrapperProps = info.href
                      ? {
                          href: info.href,
                          target: info.href.startsWith("http") ? "_blank" : undefined,
                          rel: info.href.startsWith("http") ? "noopener noreferrer" : undefined,
                        }
                      : {};
                    return (
                      <motion.li key={info.title} variants={fadeUp}>
                        <Wrapper
                          {...wrapperProps}
                          className="group flex h-full flex-col rounded-2xl border border-[#48247f]/10 bg-white p-5 shadow-[0_8px_30px_-12px_rgba(72,36,127,0.22)] transition-all duration-300 hover:-translate-y-1 hover:border-[#702cb0]/40 hover:shadow-[0_24px_48px_-12px_rgba(72,36,127,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48247f] focus-visible:ring-offset-2"
                        >
                          <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#48247f] to-[#702cb0] text-white shadow-md shadow-[#48247f]/25 transition-transform duration-300 group-hover:scale-105">
                            <Icon className="h-5 w-5" />
                          </span>
                          <h3 className="text-sm font-medium text-[#28194f]">{info.title}</h3>
                          <p className="mt-1 text-sm font-semibold text-[#28194f] break-words">
                            {info.content}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-600">{info.sub}</p>
                        </Wrapper>
                      </motion.li>
                    );
                  })}
                </motion.ul>

                {/* Connect strip — no brand logos, clean and compact */}
                <motion.div
                  variants={fadeUp}
                  initial={initial}
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                  className="relative mt-6 overflow-hidden rounded-2xl bg-gradient-to-br from-[#48247f] via-[#5a2a94] to-[#702cb0] px-5 py-6 text-white shadow-[0_20px_50px_-20px_rgba(72,36,127,0.55)] sm:px-6 sm:py-7"
                >
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/10" />
                  <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <h3 className="text-base font-medium tracking-tight sm:text-lg">
                        Connect with us
                      </h3>
                      <p className="mt-1 text-xs leading-[1.6] text-white/80 sm:text-sm">
                        Prefer a live walkthrough? We'll respond within 24 hours.
                      </p>
                    </div>
                    <a
                      href="mailto:info@deetyasoft.com"
                      className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-xs font-semibold text-[#48247f] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#f1ecfa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#48247f] sm:text-sm"
                    >
                      Email our team
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </motion.div>
              </motion.div>

              {/* RIGHT — form */}
              <motion.div
                initial={initial}
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="lg:col-span-7"
              >
                <motion.div
                  variants={fadeUp}
                  className="relative overflow-hidden rounded-2xl border border-[#702cb0]/20 bg-white p-5 shadow-[0_24px_60px_-24px_rgba(72,36,127,0.4)] ring-2 ring-[#702cb0]/20 sm:p-8 lg:p-10"
                >
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#12dfbd] via-[#702cb0] to-[#48247f]" />

                  <div className="mb-6">
                    <h2 className="text-[22px] font-light leading-[1.25] tracking-tight text-[#28194f] sm:text-[26px] md:text-[30px]">
                      Send us a{" "}
                      <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                        message
                      </span>
                    </h2>
                    <p className="mt-2 text-sm text-[#3b2a63]">
                      Fill out the form and our team will respond within 24 hours.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label htmlFor="name" className="text-xs font-semibold text-[#28194f]">
                          Full name <span className="text-[#702cb0]">*</span>
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                            <User className="h-4 w-4" />
                          </span>
                          <input
                            id="name"
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="block w-full rounded-xl border-2 border-[#48247f]/10 bg-[#fbf9ff] pl-10 pr-3 py-2.5 text-sm font-medium text-[#28194f] outline-none transition-all placeholder:text-slate-400 focus:border-[#702cb0] focus:bg-white focus:ring-2 focus:ring-[#702cb0]/20"
                            placeholder="Enter your name"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="email" className="text-xs font-semibold text-[#28194f]">
                          Email address <span className="text-[#702cb0]">*</span>
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                            <Mail className="h-4 w-4" />
                          </span>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="block w-full rounded-xl border-2 border-[#48247f]/10 bg-[#fbf9ff] pl-10 pr-3 py-2.5 text-sm font-medium text-[#28194f] outline-none transition-all placeholder:text-slate-400 focus:border-[#702cb0] focus:bg-white focus:ring-2 focus:ring-[#702cb0]/20"
                            placeholder="you@company.com"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label htmlFor="phone" className="text-xs font-semibold text-[#28194f]">
                          Phone number
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                            <Phone className="h-4 w-4" />
                          </span>
                          <input
                            id="phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="block w-full rounded-xl border-2 border-[#48247f]/10 bg-[#fbf9ff] pl-10 pr-3 py-2.5 text-sm font-medium text-[#28194f] outline-none transition-all placeholder:text-slate-400 focus:border-[#702cb0] focus:bg-white focus:ring-2 focus:ring-[#702cb0]/20"
                            placeholder="+91 90000 00000"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="company" className="text-xs font-semibold text-[#28194f]">
                          Company name
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                            <Building2 className="h-4 w-4" />
                          </span>
                          <input
                            id="company"
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="block w-full rounded-xl border-2 border-[#48247f]/10 bg-[#fbf9ff] pl-10 pr-3 py-2.5 text-sm font-medium text-[#28194f] outline-none transition-all placeholder:text-slate-400 focus:border-[#702cb0] focus:bg-white focus:ring-2 focus:ring-[#702cb0]/20"
                            placeholder="Enter your company"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="subject" className="text-xs font-semibold text-[#28194f]">
                        Subject <span className="text-[#702cb0]">*</span>
                      </label>
                      <input
                        id="subject"
                        type="text"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="block w-full rounded-xl border-2 border-[#48247f]/10 bg-[#fbf9ff] px-3 py-2.5 text-sm font-medium text-[#28194f] outline-none transition-all placeholder:text-slate-400 focus:border-[#702cb0] focus:bg-white focus:ring-2 focus:ring-[#702cb0]/20"
                        placeholder="How can we help?"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="message" className="text-xs font-semibold text-[#28194f]">
                        Message <span className="text-[#702cb0]">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        className="block w-full rounded-xl border-2 border-[#48247f]/10 bg-[#fbf9ff] px-3 py-2.5 text-sm font-medium text-[#28194f] outline-none transition-all placeholder:text-slate-400 focus:border-[#702cb0] focus:bg-white focus:ring-2 focus:ring-[#702cb0]/20 resize-none"
                        placeholder="Tell us a little about your project…"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        id="agreements"
                        type="checkbox"
                        name="agreements"
                        checked={formData.agreements}
                        onChange={handleChange}
                        required
                        className="mt-1 h-4 w-4 flex-shrink-0 rounded border-[#48247f]/30 text-[#702cb0] focus:ring-[#702cb0]"
                      />
                      <label htmlFor="agreements" className="text-xs leading-[1.7] text-[#3b2a63] sm:text-[13px]">
                        I agree to the{" "}
                        <Link to="/terms-and-conditions" className="font-medium text-[#48247f] hover:underline">
                          Terms & Conditions
                        </Link>{" "}
                        and{" "}
                        <Link to="/privacy-policy" className="font-medium text-[#702cb0] hover:underline">
                          Privacy Policy
                        </Link>{" "}
                        and consent to receive communications from Deetya Soft
                        Private Limited via SMS, email, calls, RCS messages, and
                        WhatsApp.
                      </label>
                    </div>

                    <motion.button
                      whileHover={reduced ? undefined : { scale: 1.01 }}
                      whileTap={reduced ? undefined : { scale: 0.99 }}
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#48247f] to-[#702cb0] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#48247f]/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[#48247f]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48247f] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="h-5 w-5 animate-spin text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Sending…
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Send message
                        </>
                      )}
                    </motion.button>
                  </form>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ================= VISIT US / MAP ================= */}
        <section
          aria-labelledby="visit-heading"
          className="relative isolate w-full overflow-hidden bg-[#f6f3fc] py-12 sm:py-16 lg:py-24"
        >
          <div className="pointer-events-none absolute -bottom-24 -right-24 -z-10 h-72 w-72 rounded-full bg-[#12dfbd]/15 blur-3xl sm:h-96 sm:w-96" />
          <div className="mx-auto w-full max-w-[1300px] px-4 sm:px-8 md:px-10 lg:px-14">
            <SectionHeading
              id="visit-heading"
              pill="Visit us"
              icon={MapPin}
              before="Come say"
              highlight="hello"
              text="Our team would love to meet in person. Drop by during business hours, or use the button below to open directions in Maps."
            />

            <motion.div
              initial={reduced ? false : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="relative mx-auto max-w-3xl overflow-hidden rounded-2xl border border-[#702cb0]/20 bg-white p-6 text-center shadow-[0_24px_60px_-24px_rgba(72,36,127,0.4)] ring-2 ring-[#702cb0]/20 sm:p-10"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#12dfbd] via-[#702cb0] to-[#48247f]" />
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#48247f] to-[#702cb0] text-white shadow-md shadow-[#48247f]/25">
                <MapPin className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium text-[#28194f] sm:text-xl">Our office</h3>
              <p className="mt-2 text-sm leading-[1.75] text-[#3b2a63] sm:text-[15px]">
                Mahalaxmi Square, Abhay Khand II
                <br />
                Indrapuram, Ghaziabad, Uttar Pradesh
              </p>
              <a
                href="https://maps.google.com/?q=Mahalaxmi+Square+Indrapuram+Ghaziabad"
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-6 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#48247f] to-[#702cb0] px-6 py-3 text-xs font-semibold text-white shadow-lg shadow-[#48247f]/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[#48247f]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48247f] focus-visible:ring-offset-2 sm:text-sm"
              >
                <MapPin className="h-4 w-4" />
                Get directions
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;